import { useState } from "react";
import axios from "axios";
import cnBind from "classnames/bind";
import { useFormik } from "formik";
import { usePathname } from "next/navigation";
import * as Yup from "yup";

import done from "@/shared/assests/done 1.webp";
import src_tg from "@/shared/assests/telegram.webp";
import src_max from "@/shared/assests/max.webp";
import bgImage from "@/shared/assests/photo_2024-10-03_13-29-10.webp";
import { API_BASE } from "@/shared/constants/private";
import { InputText } from "@/shared/ui/_InputText";
import { Button } from "@/shared/ui/Button";
import { CheckBox } from "@/shared/ui/CheckBox";
import { CustomImage } from "@/shared/ui/CustomImage";

import styles from "./FormFeedback.module.scss";
import { PatternFormat } from "react-number-format";

const cx = cnBind.bind(styles);

export const FormFeedback = () => {
    const href = usePathname();
    const [step, setStep] = useState(false);

    const validationSchema = Yup.object({
        name: Yup.string().required("Имя обязательно"),
        phone: Yup.string()
            .required("Номер телефона обязателен")
            .test("is-valid-phone", "Некорректный формат телефона", (value) => {
                if (!value) return false;

                const cleanValue = value.replace(/\D/g, ""); // Убираем маску

                if (cleanValue === "7" || cleanValue === "") return false;

                return cleanValue.length === 10;
            }),
        policy: Yup.boolean().oneOf([true], "Необходимо согласие с политикой в отношении обработки персональных данных"),
        personal_data: Yup.boolean().oneOf([true], "Необходимо согласие на обработку персональных данных"),
        third_personal_data: Yup.boolean().oneOf([true], "Необходимо согласие на передачу моих персональных данных третьим лицам"),
    });

    const formik = useFormik({
        initialValues: {
            name: "",
            phone: "",
            policy: false,
            personal_data: false,
            third_personal_data: false
        },
        validationSchema,
        validateOnChange: false,
        validateOnBlur: true,
        onSubmit: async (values, { setSubmitting }) => {
            try {
                await axios.post(`${API_BASE}/mail`, { phone: `+7${values.phone}`, name: `${values.name} страница:${href}`, policy: values.policy });
                formik.resetForm();
                setStep(true);
            } catch (error) {
                console.error("Ошибка при отправке формы:", error);
            } finally {
                setSubmitting(false);
            }
        },
    });

    return (
        <div className={cx("helper-block")}>
            <div className={cx("wrapper", "container")}>
                <div className={cx("form-container")}>
                    <CustomImage
                        src={bgImage}
                        alt="Фон формы"
                        fill
                        style={{ objectFit: "cover" }}
                        className={cx("form-bg-image")} // Этот класс сделает картинку фоном
                    />

                    <div className={cx("form-header")}>
                        <h2>{!step ? "Задавайте вопросы" : "Заявка отправлена"}</h2>
                        <p>
                            {!step
                                ? "Если вы не нашли ответов на свои вопросы — напишите нам, и мы ответим вам в ближайшее время"
                                : "Мы свяжемся с вами в кратчайшее время и ответим на все интересующие вас вопросы"
                            }
                        </p>
                    </div>

                    <form className={cx("form-body")} onSubmit={formik.handleSubmit} noValidate>
                        {!step ? (
                            <>
                                <div className={cx("inputs-row")}>
                                    <div className={cx("input-field-container")}>
                                        <InputText
                                            value={formik.values.name}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            name="name"
                                            placeholder="Имя"
                                            isFullWidth
                                        />
                                        {formik.touched.name && formik.errors.name && (
                                            <span className={cx("form-error-message")}>{formik.errors.name}</span>
                                        )}
                                    </div>
                                    <div className={cx("input-field-container")}>
                                        <PatternFormat
                                            format="+7 (###) ###-##-##"
                                            allowEmptyFormatting={true}
                                            mask="_"
                                            type="tel"
                                            name="phone"
                                            placeholder="Номер телефона*"
                                            isFullWidth
                                            value={formik.values.phone}

                                            onValueChange={(values) => {
                                                formik.setFieldValue("phone", values.value);
                                            }}
                                            onBlur={formik.handleBlur}

                                            customInput={InputText}
                                        />
                                        {formik.touched.phone && formik.errors.phone && (
                                            <span className={cx("form-error-message")}>{formik.errors.phone}</span>
                                        )}
                                    </div>
                                </div>

                                <div className={cx("form-footer")}>
                                    <CheckBox
                                        checked={formik.values.policy}
                                        onChange={formik.handleChange}
                                        name="policy"
                                        title="Ознакомлен с "
                                        link="политикой в отношении обработки персональных данных"
                                        url="/politika-konfidentsialnosti"
                                    />
                                    <CheckBox
                                        checked={formik.values.personal_data}
                                        onChange={formik.handleChange}
                                        name="personal_data"
                                        title="Даю согласие на обработку "
                                        link="моих персональных данных (имя, телефон) для выполнения моего запроса"
                                        url="/accept-personal-data"
                                    />
                                    <CheckBox
                                        checked={formik.values.third_personal_data}
                                        onChange={formik.handleChange}
                                        name="third_personal_data"
                                        title="Даю согласие на передачу "
                                        link="моих персональных данных (имя, телефон) лицам, обрабатывающим их по поручению Оператора в целях исполнения запроса."
                                        url="/accept-third-personal-data"
                                    />

                                    <div className={cx("social")}>
                                        <button
                                            type="button"
                                            onClick={() => window.open("https://max.ru/u/f9LHodD0cOKEIQ90ZSTukfL61T1F9ZPXCnD6dnVQgDd-TIatgt9F6cH863Q", "_blank")}
                                            className={cx("social-item", "icon")}
                                            aria-label="Написать в MAX"
                                        >
                                            <CustomImage src={src_max} alt="Max" />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => window.open("https://t.me/liftmediagroup", "_blank")}
                                            className={cx("social-item", "icon")}
                                            aria-label="Написать в Telegram"
                                        >
                                            <CustomImage src={src_tg} alt="Telegram" />
                                        </button>
                                    </div>
                                </div>

                                <Button
                                    disabled={!formik.values.policy || !formik.values.personal_data || !formik.values.third_personal_data || !formik.values.name || !formik.values.phone || formik.values.phone.length < 10 || formik.isSubmitting}
                                    className={cx("button")}
                                    label="Отправить заявку"
                                    type="submit"
                                />
                            </>
                        ) : (
                            <div className={cx("success-block")}>
                                <div className={cx("social-item", "icon", "done-icon")}>
                                    <CustomImage src={done} alt="done" />
                                </div>
                                <Button
                                    className={cx("button", "back-button")}
                                    label="Назад"
                                    type="button"
                                    onClick={() => setStep(false)}
                                />
                            </div>
                        )}
                    </form>
                </div>
            </div>
        </div>
    );
};
